import {
  Box,
  ROW_RUNGS,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import React from "react";
import {
  teamRefToEntity,
  userRefToEntity,
  type EntityData,
} from "#app/lib/entity-visuals";
import type {
  AccessSummaryMember,
  AccessSummaryTeam,
} from "#app/lib/hooks/use-object-sharing";
import { ACCESS_LEVEL_LABEL } from "./access-level-menu";
import { ACCESS_ROW_HEIGHT } from "./access-row";

// Rows under an opened workspace row or team sit one rung tighter.
export const NESTED_ROW_HEIGHT = ROW_RUNGS.control;

export function memberEntity(member: AccessSummaryMember): EntityData {
  return userRefToEntity({
    userId: member.id,
    name: member.name,
    username: member.username,
    email: member.email,
    profileImage: member.profileImage,
    memberStatus: member.memberStatus,
  });
}

export function teamEntity(team: AccessSummaryTeam): EntityData {
  return teamRefToEntity({
    teamId: team.id,
    teamName: team.name,
    icon: { icon: team.icon, color: team.iconColor },
  });
}

// The role a row wears as a badge: the owner and admins name the role that
// locks their tier; a share-only role ("Guest", "Limited Member") says this
// person reaches the subject only through the list. A member on a
// public-access role wears nothing — their row is an exception, and the
// tier speaks for itself.
export function roleBadge(member: AccessSummaryMember): string | undefined {
  if (member.isWorkspaceOwner) return "Owner";
  if (member.locked) return "Admin";
  if (!member.hasPublicAccess) return member.roleName ?? undefined;
  return undefined;
}

// A derived path, named in muted text after the badge: the row exists
// because of something other than a share, and removing that removes the
// access.
export function memberMeta(member: AccessSummaryMember): string | undefined {
  if (!member.enabled) return undefined;
  switch (member.source) {
    case "assignee":
      return "Assignee";
    case "team":
      return "Via team";
    default:
      return undefined;
  }
}

// An accordion header on the compact trigger rung. The outer frame puts
// the content back on the panel's column (the accordion around it bleeds
// to the dialog's edge so the band below can run edge to edge) and carries
// the gap between rows, so the space grows without the hover growing. The
// wash is the inner, rounded box: it reaches one small step past the
// column on each side and stops short of the dialog's edge, so it reads as
// a row's hover, not a stripe across the dialog. The trigger fills the row,
// so a press anywhere on the wash that is not a control opens it. A row
// on the same rhythm that opens nothing (the link row) takes `hover`
// off: same column, same pitch, no wash to promise a press.
export function HeaderRow({
  children,
  hover = true,
}: {
  children: React.ReactNode;
  hover?: boolean;
}) {
  return (
    <Box paddingX="lg" paddingY="2xs">
      <Box
        direction="row"
        align="center"
        justify="between"
        gap="base"
        hoverBackground={hover}
        marginXNegative="sm"
        minHeight={ACCESS_ROW_HEIGHT}
        paddingX="sm"
        rounded="sm"
      >
        {children}
      </Box>
    </Box>
  );
}

// Owners and admins hold full edit through their role; the dialog states
// it instead of offering a control it would have to disable.
export function LockedLevel() {
  return (
    <StaticLevel
      label={ACCESS_LEVEL_LABEL.full_edit}
      hint="Owners and admins always have full access"
    />
  );
}

// A tier the dialog states rather than offers: the reason on hover.
export function StaticLevel({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Box paddingX="sm" height={28} justify="center" />}>
        <Text size="sm" variant="muted">
          {label}
        </Text>
      </TooltipTrigger>
      <TooltipPopup>{hint}</TooltipPopup>
    </Tooltip>
  );
}
